import { useCallback } from 'react';
import { formatDateForFilename, sanitizeFilename } from '@orca360/shared';
import { useDocumentAPI } from './useDocumentAPI';
import type { PilotAssessmentExportData } from '../types/pilot-assessment-export';

export interface UseWordExportPilotAssessmentOptions {
  onExportStart?: () => void;
  onExportSuccess?: (blob: Blob, filename: string) => void;
  onExportError?: (error: Error) => void;
}

export interface UseWordExportPilotAssessmentReturn {
  exportPilotAssessmentToWord: (data: PilotAssessmentExportData, sessionId?: string) => Promise<Blob>;
  isExporting: boolean;
  error: Error | null;
}

export function useWordExportPilotAssessment(
  options: UseWordExportPilotAssessmentOptions = {},
): UseWordExportPilotAssessmentReturn {
  const { generateDocument, isExporting, error } = useDocumentAPI(options);

  const exportPilotAssessmentToWord = useCallback(
    async (data: PilotAssessmentExportData, sessionId?: string): Promise<Blob> => {
      const roleLabel =
        data.metadata?.pilotRole === 'captain' ? 'Captain'
        : data.metadata?.pilotRole === 'captain_2' ? 'Captain_2'
        : data.metadata?.pilotRole === 'first_officer_2' ? 'First_Officer_2'
        : 'First_Officer';
      const defaultFilename = `WingMentor_${roleLabel}_Assessment_${formatDateForFilename(data.partA.sessionDate)}_${sanitizeFilename(data.partA.trainee.name)}.docx`;
      // sessionId lets the endpoint resolve the owning tenant's effective
      // framework version for authority exports (HG-2B G2-05d).
      return generateDocument('pilot-assessment', 'docx', data as unknown as Record<string, unknown>, defaultFilename, sessionId);
    },
    [generateDocument],
  );

  return { exportPilotAssessmentToWord, isExporting, error };
}
